/**
 * Benefits — 인증으로 받을 수 있는 구체적 혜택 (슬라이드 10장)
 *
 * preview/index.html 의 .benefits 1:1 매칭
 *  - 5개씩 한 페이지, 총 10개 = 2페이지 (데스크톱)
 *  - 자동 슬라이드 5초, viewport hover 시 일시정지
 *  - 좌우 화살표 + 도트 페이지네이션
 *
 * 데이터: BENEFITS (config/constants.ts)
 * 슬라이드 로직: useBenefitsSlider (hooks/)
 *
 * 아이콘 처리:
 *  - 카드별 SVG path 마크업을 상수로 보관 (BENEFITS[i].iconSvg)
 *  - dangerouslySetInnerHTML 로 <svg> 내부에 주입
 *  - 외부 입력이 아닌 하드코딩된 상수라 XSS 위험 없음
 */
import { BENEFITS } from '@/config/constants';
import { useBenefitsSlider } from '@/hooks/useBenefitsSlider';

export function Benefits() {
  const slider = useBenefitsSlider({ totalCards: BENEFITS.length, intervalMs: 5000 });

  return (
    <section className="benefits" id="benefits">
      <div className="section-head">
        <span className="section-tag">BENEFITS</span>
        <h2 className="section-title">
          인증으로 받을 수 있는<br />
          <span className="accent">구체적인 혜택</span>
        </h2>
        <p className="section-sub">
          벤처인증·연구소 설립·ISO인증·경리아웃소싱 시 적용 가능한 주요 혜택입니다.
        </p>
      </div>

      <div className="benefits-viewport" {...slider.bindViewport}>
        <button
          type="button"
          className="benefits-arrow prev"
          aria-label="이전 슬라이드"
          onClick={slider.prev}
        >
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          type="button"
          className="benefits-arrow next"
          aria-label="다음 슬라이드"
          onClick={slider.next}
        >
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        <div className="benefits-track-wrap">
          <div className="benefits-track" style={slider.trackStyle}>
            {BENEFITS.map((b) => (
              <article key={b.num} className="benefit-card">
                <div className="benefit-num">{b.num}</div>
                <div className="benefit-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    // 하드코딩된 상수만 주입 (XSS 위험 없음)
                    dangerouslySetInnerHTML={{ __html: b.iconSvg }}
                  />
                </div>
                <h3 className="benefit-title">{b.title}</h3>
                <span className="benefit-tag">{b.tag}</span>
                <p
                  className="benefit-desc"
                  // <br/> 포함된 상수 → 안전
                  dangerouslySetInnerHTML={{ __html: b.desc }}
                />
                {b.note && <p className="benefit-note">{b.note}</p>}
              </article>
            ))}
          </div>
        </div>

        <div className="benefits-dots">
          {Array.from({ length: slider.pageCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`benefits-dot${i === slider.current ? ' active' : ''}`}
              aria-label={`슬라이드 ${i + 1}`}
              onClick={() => slider.goTo(i)}
            />
          ))}
        </div>

        {/* 모바일 전용 페이지네이션 — 화살표 + 점 + "01 / 10" 카운터 */}
        <div className="benefits-mobile-pager" aria-hidden="false">
          <button
            type="button"
            className="benefits-mobile-arrow"
            aria-label="이전 슬라이드"
            onClick={slider.prev}
          >
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="benefits-mobile-dot" aria-hidden="true" />
          <span className="benefits-mobile-counter">
            {String(slider.current + 1).padStart(2, '0')} / {String(slider.pageCount).padStart(2, '0')}
          </span>
          <button
            type="button"
            className="benefits-mobile-arrow"
            aria-label="다음 슬라이드"
            onClick={slider.next}
          >
            <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" stroke="currentColor">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
